import { Member, LeagueMode } from "./types";

/** Syndicate breaks ties by "closest predicted total goals" within the league's
 * scoring window. */
function tiebreakerDiff(m: Member, tournamentGoals: number): number | null {
  if (m.syndicateTiebreakerGoals == null) return null;
  return Math.abs(m.syndicateTiebreakerGoals - tournamentGoals);
}

export function sortMembers(members: Member[], mode: LeagueMode, tournamentGoals?: number): Member[] {
  return [...members].sort((a, b) => {
    if (mode === "TARGET") {
      const aBust = a.targetState === "BUST";
      const bBust = b.targetState === "BUST";
      if (aBust !== bBust) return aBust ? 1 : -1;
      if (aBust && bBust) return a.score - b.score;
    }
    if (b.score !== a.score) return b.score - a.score;
    if (mode === "SYNDICATE" && tournamentGoals !== undefined) {
      const aDiff = tiebreakerDiff(a, tournamentGoals);
      const bDiff = tiebreakerDiff(b, tournamentGoals);
      if (aDiff !== null && bDiff !== null) return aDiff - bDiff;
      if (aDiff !== null) return -1;
      if (bDiff !== null) return 1;
    }
    return 0;
  });
}

function areRankTied(a: Member, b: Member, mode: LeagueMode, tournamentGoals?: number): boolean {
  if (a.score !== b.score) return false;
  if (mode === "SYNDICATE" && tournamentGoals !== undefined) {
    const aDiff = tiebreakerDiff(a, tournamentGoals);
    const bDiff = tiebreakerDiff(b, tournamentGoals);
    if (aDiff !== null && bDiff !== null) return aDiff === bDiff;
    if (aDiff !== null || bDiff !== null) return false;
  }
  return true;
}

export function computeRanks(members: Member[], mode: LeagueMode, tournamentGoals?: number): Map<string, string> {
  const sorted = sortMembers(members, mode, tournamentGoals);

  const rawRank = new Map<string, number>();
  sorted.forEach((m, i) => {
    rawRank.set(
      m.id,
      i === 0 || !areRankTied(sorted[i - 1], m, mode, tournamentGoals)
        ? i + 1
        : rawRank.get(sorted[i - 1].id)!,
    );
  });

  const rankCount = new Map<number, number>();
  rawRank.forEach((r) => rankCount.set(r, (rankCount.get(r) ?? 0) + 1));

  const ranks = new Map<string, string>();
  rawRank.forEach((r, id) => ranks.set(id, rankCount.get(r)! > 1 ? `${r}=` : `${r}`));
  return ranks;
}

export interface GwRecapNeighbour {
  displayName: string;
  score: number;
  /** Always positive — the neighbour's score minus mine (above) or mine minus theirs (below).
   * "Points needed to overtake" is this gap taken at face value, not a prediction of the
   * neighbour's own future score. */
  gap: number;
}

/**
 * Finds the members immediately above and below `myMemberId` in an already-sorted leaderboard
 * (pass the result of `sortMembers` — this does not re-sort). Works for every mode, including
 * SCORE_PREDICTOR and SYNDICATE, where the *backend's* rankMemberInLeague deliberately returns
 * null — that limitation is about the server-computed integer rank, not about whether a
 * position in a client-sorted list exists, so it doesn't apply here.
 */
export function getGwRecapNeighbours(
  sortedMembers: Member[],
  myMemberId: string,
): { above: GwRecapNeighbour | null; below: GwRecapNeighbour | null } {
  const myIndex = sortedMembers.findIndex((m) => m.id === myMemberId);
  if (myIndex === -1) return { above: null, below: null };

  const me = sortedMembers[myIndex];
  const aboveMember = myIndex > 0 ? sortedMembers[myIndex - 1] : null;
  const belowMember = myIndex < sortedMembers.length - 1 ? sortedMembers[myIndex + 1] : null;

  return {
    above: aboveMember ? { displayName: aboveMember.displayName, score: aboveMember.score, gap: aboveMember.score - me.score } : null,
    below: belowMember ? { displayName: belowMember.displayName, score: belowMember.score, gap: me.score - belowMember.score } : null,
  };
}
